import {
    BreadcrumbsButton,
    BreadcrumbsGroup,
    Button,
    FlexScrollArea,
    FlexScrollAreaContainer,
    Grid,
    Stack,
    StickyFabPlacementHelper,
} from "@biggeo/bg-ui/lab";
import { CenteredLayout } from "@biggeo/bg-ui/lab/layouts";
import { Prettify } from "@biggeo/bg-utils";
import { ComponentProps, ReactNode } from "react";

export type DataManagementAddandIndexLayoutProps = {
    sidePanel: ReactNode;
    content: ReactNode;
    breadcrumbs?: ReadonlyArray<
        Prettify<
            Readonly<
                {
                    label: string;
                } & Pick<
                    ComponentProps<typeof BreadcrumbsButton>,
                    "onClick" | "href" | "sx"
                >
            >
        >
    >;
    onClickGenerate?: () => void;
    disabled?: boolean;
};

const offsetY = "186px";

export const DataManagementAddandIndexLayout = ({
    sidePanel,
    content,
    breadcrumbs,
    onClickGenerate,
    disabled
}: DataManagementAddandIndexLayoutProps) => {
    return (
        <FlexScrollAreaContainer width="100%">
            {breadcrumbs && (
                <Grid
                    container
                    sx={{
                        padding: 4,
                        borderBottom: 1,
                        borderColor: (theme) => theme.palette.stroke[100],
                    }}
                    alignItems="center"
                >
                    <BreadcrumbsGroup
                        value={breadcrumbs[breadcrumbs.length - 1]?.label}
                    >
                        {breadcrumbs.map(({ label, ...buttonProps }, i) => (
                            <BreadcrumbsButton
                                key={label}
                                hideSeparator={i === breadcrumbs.length - 1}
                                value={label}
                                {...buttonProps}
                            >
                                {label}
                            </BreadcrumbsButton>
                        ))}
                    </BreadcrumbsGroup>
                </Grid>
            )}
            <FlexScrollArea width="100%">
                <CenteredLayout
                    maxWidth={245}
                    sx={{
                        height: "auto",
                        breakpoints: {
                            xs: {
                                paddingY: 3,
                                paddingX: 4,
                            },
                            md: {
                                padding: 8,
                            },
                        },
                    }}
                    slotProps={{
                        main: {
                            sx: {
                                marginY: "unset",
                            },
                        },
                    }}
                >
                    <Grid container gap={8} alignItems="flex-start">
                        <Grid item xs minWidth={0}>
                            <Stack width="100%" gap={8} alignItems="flex-start">
                                {content}
                                <Stack
                                    width="100%"
                                    sx={{
                                        borderRadius: (theme) =>
                                            theme.radius.xs3,
                                        border: 1,
                                        borderColor: (theme) =>
                                            theme.palette.stroke[100],
                                        backgroundColor: (theme) =>
                                            theme.palette.background.container,
                                        boxShadow: (theme) =>
                                            theme.shadows.allAround,
                                        breakpoints: {
                                            md: {
                                                display: "none",
                                            },
                                        },
                                    }}
                                >
                                    {sidePanel}
                                </Stack>
                            </Stack>
                        </Grid>
                        <Grid
                            item
                            sx={{
                                display: "none",
                                breakpoints: {
                                    md: {
                                        display: "flex",
                                        maxWidth: 75,
                                        width: "100%",
                                    },
                                },
                                borderRadius: (theme) => theme.radius.xs3,
                                border: 1,
                                borderColor: (theme) =>
                                    theme.palette.stroke[100],
                                backgroundColor: (theme) =>
                                    theme.palette.background.container,
                                boxShadow: (theme) => theme.shadows.allAround,
                                position: "sticky",
                                top: 8,
                                maxHeight: `calc(100dvh - ${offsetY})`,
                                minHeight: 0,
                                overflowY: "auto",
                            }}
                        >
                            {sidePanel}
                        </Grid>
                    </Grid>
                    {onClickGenerate && (
                        <StickyFabPlacementHelper
                            sx={{
                                paddingBottom: 4,
                                breakpoints: {
                                    xs: {
                                        paddingTop: 7,
                                    },
                                    md: {
                                        paddingTop: 12,
                                    },
                                },
                            }}
                        >
                            <Button onClick={onClickGenerate} color="surface" disabled={disabled}>
                                Generate New Sources
                            </Button>
                        </StickyFabPlacementHelper>
                    )}
                </CenteredLayout>
            </FlexScrollArea>
        </FlexScrollAreaContainer>
    );
};
